import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FormContext, useForm } from 'react-hook-form';
import CircularProgress from '@material-ui/core/CircularProgress';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { useSendoutAccountability } from 'features/sendouts/hooks';
import { useSendoutCandidateHydration } from 'features/sendouts/hooks/useSendoutCandidateHydration';
import { useFetchWithStatusV2 } from 'hooks/fetchWithStatus';
import { useUserContext } from 'providers/UserProvider';
import { userHasSomeRoles } from 'services/Authorization';
import strings from 'strings';
import { When } from 'UI/components/atoms';
import FPButtonBuilder from 'UI/components/molecules/FPButtonBuilder';
import HighlightBox from 'UI/components/molecules/HighlightBox';
import DecisionDialog from 'UI/components/organisms/DecisionDialog';
import EmptyPlaceholder from 'UI/components/templates/EmptyPlaceholder';
import FPDrawer from 'UI/components/templates/FPDrawer/FPDrawer';
import { Endpoints } from 'UI/constants/endpoints';
import { CompanyEntity, JobOrderEntity } from 'UI/constants/entityTypes';
import { Roles } from 'UI/constants/roles';
import { UIStatus } from 'UI/constants/status';

import { JobOrderDetails } from '../../shared';
import { JOB_ORDER_INCLUDES } from '../CreateSendoutDrawer/CreateSendoutDrawer.constants';
import { getInterviewTimeValidationError } from '../CreateSendoutDrawer/CreateSendoutDrawer.utils';
import { SendoutForm } from '../CreateSendoutDrawer/SendoutForm';
import {
  CONFIRMATION_DIALOGS,
  FORM_FIELDS_MAP
} from '../CreateSendoutDrawer/SendoutForm/SendoutForm.constants';
import {
  ATTACHMENT_FIELD_KEYS,
  buildAttachmentFieldValues
} from '../CreateSendoutDrawer/SendoutForm/SendoutForm.utils';

import { SWITCH_SENDOVER_TO_SENDOUT_TYPES } from './ConvertSendoverToSendoutDrawer.constants';
import { LoaderContainer, NotificationTextDiv } from './ConvertSendoverToSendoutDrawer.styled';
import {
  buildConvertSendoutDefaultValues,
  buildConvertSendoverToSendoutSubmissionData,
  getSelectedAttachments
} from './ConvertSendoverToSendoutDrawer.utils';

export const ConvertSendoverToSendoutDrawer = ({
  jobOrderId = null,
  isSubmitting = false,
  onClose = () => {},
  onSaved = () => {},
  onViewDuplicates = () => {},
  sendover = {},
  switchType = SWITCH_SENDOVER_TO_SENDOUT_TYPES.WithMail
}) => {
  const [currentUser] = useUserContext();
  const isWithoutMail = switchType === SWITCH_SENDOVER_TO_SENDOUT_TYPES.WithoutMail;
  const form = useForm({
    defaultValues: buildConvertSendoutDefaultValues({ sendover, switchType })
  });

  const [candidateDuplicateInfo, setCandidateDuplicateInfo] = useState({
    isDuplicate: false,
    duplicates: [],
    error: null,
    shouldBlock: false
  });
  const [candidateInfo, setCandidateInfo] = useState({
    name: sendover?.candidate?.personalInformation?.first_name || null,
    phone: sendover?.candidate?.personalInformation?.contact?.phone || null
  });
  const [pendingSubmissionData, setPendingSubmissionData] = useState(null);
  const [activeDialog, setActiveDialog] = useState(null);
  const [uiState, setUIState] = useState({
    hasCandidateHydrationError: false
  });
  const hasHydratedCandidateRef = useRef(false);

  const { state } = useFetchWithStatusV2({
    endpoint: `${Endpoints.JobOrders}/${jobOrderId}?includes=${JOB_ORDER_INCLUDES}`,
    apiVersion: 2
  });

  const jobOrder = state.results || {};

  const { canConvertSendover } = useSendoutAccountability(sendover);
  const canSkipHiringAuthorityEmail = userHasSomeRoles(currentUser, [Roles.Operations]);

  const { clearError, handleSubmit, register, setError, setValue, unregister } = form;
  const {
    candidateAttachments,
    hydrateCandidate,
    hydratedCandidateId,
    isHydratingCandidate,
    recruiterOptions
  } = useSendoutCandidateHydration({
    jobOrderId,
    onCandidateInfoChange: setCandidateInfo,
    onDuplicateChange: setCandidateDuplicateInfo
  });

  const isFetching = state.status === UIStatus.Loading;
  const hasErrorInInitialFetching = state.status === UIStatus.Error;
  const hasLoadingError = hasErrorInInitialFetching || uiState.hasCandidateHydrationError;
  const canRenderForm = canConvertSendover && !hasLoadingError;

  const syncAttachmentFields = useCallback(
    attachments => {
      const attachmentFieldValues = buildAttachmentFieldValues(attachments);

      ATTACHMENT_FIELD_KEYS.forEach(fieldKey => {
        setValue(fieldKey, attachmentFieldValues[fieldKey], false);
      });
    },
    [setValue]
  );

  useEffect(() => {
    if (!isWithoutMail) return undefined;

    register(FORM_FIELDS_MAP.HiringAuthority.key);

    return () => {
      unregister(FORM_FIELDS_MAP.HiringAuthority.key);
    };
  }, [isWithoutMail, register, unregister]);

  useEffect(() => {
    let isMounted = true;

    if (
      hasHydratedCandidateRef.current ||
      state.status !== UIStatus.Success ||
      !sendover?.candidate?.id
    ) {
      return () => {
        isMounted = false;
      };
    }

    hydrateCandidate({
      candidateId: sendover.candidate.id,
      disableDuplicateBlocking: true
    }).then(({ attachments, error }) => {
      if (!isMounted) return;
      if (error) {
        setUIState(prev => ({ ...prev, hasCandidateHydrationError: true }));
        return;
      }

      hasHydratedCandidateRef.current = true;
      syncAttachmentFields(attachments);
    });

    return () => {
      isMounted = false;
    };
  }, [hydrateCandidate, sendover?.candidate?.id, setUIState, state.status, syncAttachmentFields]);

  const sendoutInfo = {
    jobOrder: {
      id: jobOrder?.id,
      title: JobOrderEntity.singular,
      name: jobOrder?.title
    },
    company: {
      id: jobOrder?.company?.id,
      title: CompanyEntity.singular,
      name: jobOrder?.company?.name
    }
  };

  const closeConfirmationFlow = () => {
    setActiveDialog(null);
    setPendingSubmissionData(null);
  };

  const openFinalConfirmationDialog = () => {
    if (isWithoutMail) {
      setActiveDialog(CONFIRMATION_DIALOGS.WithoutMailConfirmation);
      return;
    }

    setActiveDialog(CONFIRMATION_DIALOGS.Preview);
  };

  const handleSave = formData => {
    const interviewTimeValidationError = getInterviewTimeValidationError(formData);

    if (interviewTimeValidationError) {
      setError(
        interviewTimeValidationError.fieldKey,
        'validate',
        interviewTimeValidationError.message
      );
      return;
    }

    clearError(FORM_FIELDS_MAP.InterviewFromHour.key);
    clearError(FORM_FIELDS_MAP.InterviewToHour.key);

    const nextSubmissionData = buildConvertSendoverToSendoutSubmissionData({
      formData,
      jobOrder,
      candidateInfo,
      switchType
    });
    const selectedAttachments = getSelectedAttachments(formData);
    const hasNoAttachments = selectedAttachments.length === 0;

    setPendingSubmissionData(nextSubmissionData);

    if (hasNoAttachments) {
      setActiveDialog(CONFIRMATION_DIALOGS.NoAttachments);
      return;
    }

    openFinalConfirmationDialog();
  };

  const submitConversion = async nextSubmissionData => {
    if (!nextSubmissionData) return;

    await onSaved(nextSubmissionData);
  };

  const handleConfirmPreview = async () => {
    await submitConversion(pendingSubmissionData);
  };

  const buttonDefinitions = [
    {
      props: {
        text: strings.shared.ui.cancel,
        variant: 'outlined',
        onClick: onClose,
        disabled: isSubmitting
      },
      GridItemProps: { md: 3 }
    },
    {
      props: {
        text: strings.shared.ui.save,
        variant: 'contained',
        onClick: handleSubmit(handleSave),
        disabled:
          isFetching ||
          isHydratingCandidate ||
          candidateDuplicateInfo.shouldBlock ||
          isSubmitting ||
          hasErrorInInitialFetching,
        isSaving: isSubmitting
      },
      GridItemProps: { md: 3 },
      mode: 'save',
      visible: canRenderForm
    }
  ];

  const handleClose = isSubmitting ? () => {} : onClose;

  return (
    <>
      <FPDrawer
        FPDrawerProps={{ open: true }}
        CardHeaderProps={{ title: strings.sendouts.tab.actions.createSendout }}
        onClose={handleClose}
        footer={
          <FPButtonBuilder
            GridContainerProps={{ spacing: 2 }}
            buttonDefinitions={buttonDefinitions}
          />
        }
      >
        <When condition={isFetching}>
          <LoaderContainer>
            <CircularProgress />
          </LoaderContainer>
        </When>
        <When condition={!isFetching}>
          <When condition={!hasLoadingError && !canConvertSendover}>
            <EmptyPlaceholder
              subtitle={strings.formatString(strings.sendoutsV2.form.emptyPlaceholderText, {
                documentType: 'Sendout'
              })}
              height="100%"
            />
          </When>
          <When condition={hasLoadingError}>
            <EmptyPlaceholder
              subtitle={strings.formatString(strings.sendoutsV2.form.initialFetchingError, {
                documentType: 'Sendout'
              })}
              height="100%"
            />
          </When>
          <When condition={canRenderForm}>
            <FormContext {...form}>
              <JobOrderDetails company={sendoutInfo.company} jobOrder={sendoutInfo.jobOrder} />
              <SendoutForm
                candidateAttachments={candidateAttachments}
                candidateHydratedId={hydratedCandidateId}
                candidateInfo={candidateInfo}
                canSkipHiringAuthorityEmail={canSkipHiringAuthorityEmail}
                duplicateInfo={candidateDuplicateInfo}
                hydrateCandidate={hydrateCandidate}
                isCandidateLocked
                isHydratingCandidate={isHydratingCandidate}
                jobOrder={jobOrder}
                recruiterOptions={recruiterOptions}
                showAttachments={!isWithoutMail}
                showDuplicateWarning={false}
                showEmailDetails={!isWithoutMail}
                onViewDuplicates={onViewDuplicates}
              />
            </FormContext>
          </When>
        </When>
      </FPDrawer>
      <When condition={activeDialog === CONFIRMATION_DIALOGS.NoAttachments}>
        <DecisionDialog
          cancelButtonText={strings.shared.ui.cancel}
          confirmButtonText={strings.shared.ui.send}
          isOpened
          onClose={closeConfirmationFlow}
          onConfirm={openFinalConfirmationDialog}
          severity="warning"
          showCloseButton={false}
          showSeverityIcon
          title={strings.sendoutsV2.alerts.confirmationDialogs.noAttachments.title}
          withButtons="YesNo"
          message={strings.sendoutsV2.alerts.confirmationDialogs.noAttachments.message}
        />
      </When>
      <When
        condition={
          activeDialog === CONFIRMATION_DIALOGS.Preview &&
          switchType === SWITCH_SENDOVER_TO_SENDOUT_TYPES.WithMail
        }
      >
        <DecisionDialog
          cancelButtonText={strings.shared.ui.cancel}
          confirmButtonText={strings.shared.ui.singleConfirm}
          isOpened
          mustComplete
          fetching={isSubmitting}
          onClose={closeConfirmationFlow}
          onConfirm={handleConfirmPreview}
          severity="info"
          showCloseButton={false}
          showSeverityIcon
          title={strings.sendoutsV2.alerts.confirmationDialogs.emailConfirmation.title}
          message={strings.sendoutsV2.alerts.confirmationDialogs.emailConfirmation.message}
          withButtons="YesNo"
        >
          <HighlightBox
            text={pendingSubmissionData?.confirmation?.resolvedEmailBody || ''}
            highlightData={pendingSubmissionData?.confirmation?.resolvedTagValues || []}
          />
        </DecisionDialog>
      </When>
      <When
        condition={activeDialog === CONFIRMATION_DIALOGS.WithoutMailConfirmation && isWithoutMail}
      >
        <DecisionDialog
          cancelButtonText={strings.shared.ui.cancel}
          confirmButtonText={strings.shared.ui.singleConfirm}
          isOpened
          mustComplete
          fetching={isSubmitting}
          onClose={closeConfirmationFlow}
          onConfirm={handleConfirmPreview}
          severity="warning"
          showCloseButton={false}
          showSeverityIcon
          title={strings.sendoutsV2.alerts.confirmationDialogs.conversionWithoutMail.title}
          message={strings.sendoutsV2.alerts.confirmationDialogs.conversionWithoutMail.message}
          withButtons="YesNo"
        >
          {pendingSubmissionData?.confirmation?.summaryItems?.map(({ label, value }) => (
            <Grid container spacing={1} key={label}>
              <Grid item xs={4}>
                <Typography variant="body1">{label}:</Typography>
              </Grid>
              <Grid item xs={8}>
                <Typography variant="body2">{value}</Typography>
              </Grid>
            </Grid>
          ))}
          <NotificationTextDiv>
            <Typography component="span" variant="body2">
              <b>
                {
                  strings.sendoutsV2.alerts.confirmationDialogs.conversionWithoutMail
                    .notificationSubtitleText
                }
              </b>{' '}
              {strings.sendoutsV2.alerts.confirmationDialogs.conversionWithoutMail.notificationText}
            </Typography>
          </NotificationTextDiv>
        </DecisionDialog>
      </When>
    </>
  );
};
